/**
 * createPMREMEnvironment() — procedural studio PMREM environment map.
 *
 * Builds a 4-PointLight chromatic-hot-spot environment (warm key + cool fill +
 * white top + warm kicker) that produces realistic reflections and specular
 * highlights on metallic/glossy frame surfaces. Used by the eyewear VTO and
 * product viewer for visual consistency.
 *
 * Why PointLights, not DirectionalLights: Point lights appear as bright spots
 * in the baked cubemap → directional specular hot spots on reflective surfaces.
 * Directional lights produce uniform IBL → flat, plastic-looking metallics.
 * (See commit 4dcec0474 — switching from 5 DirLights to 4 PointLights fixed a
 * regression where eyewear rendered grey/plastic instead of glossy.)
 *
 * The jewelry scene uses directional lights only (no PMREM) — this utility is
 * intentionally opt-in, not auto-applied.
 *
 * Usage:
 *   const THREE = await import('three');
 *   const envTexture = createPMREMEnvironment(THREE, renderer, scene);
 *   // envTexture is also assigned to scene.environment
 */

import type { WebGLRenderer, Scene, Texture } from 'three';
import type { ThreeModule } from './types.js';

/**
 * Build a procedural PMREM environment map and assign it to the scene.
 *
 * Constructs a tiny virtual "studio" scene with 5 lights (key + fill +
 * rim + bounce + kicker) plus a hemisphere light for ambient lift, then
 * uses PMREMGenerator.fromScene() to create a pre-filtered environment
 * texture for image-based lighting.
 *
 * Returns the environment texture, or null if PMREMGenerator is
 * unavailable (graceful degradation — direct lights still illuminate).
 *
 * @param THREE    - Dynamically imported Three.js module.
 * @param renderer - Active WebGLRenderer (needed by PMREMGenerator).
 * @param scene    - Scene to assign the environment map to.
 * @param intensity - Environment intensity multiplier. Default: 1.5.
 * @returns The PMREM texture, or null on failure.
 */
export function createPMREMEnvironment(
  THREE: ThreeModule,
  renderer: WebGLRenderer,
  scene: Scene,
  intensity = 1.5,
): Texture | null {
  try {
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    const envScene = new THREE.Scene() as Scene;

    // Use PointLights — they appear as bright spots in the cubemap, creating
    // the warm/cool specular hot spots that make metallic frames look glossy.
    // DirectionalLights produce uniform IBL (no position variation) which
    // looks flat on reflective surfaces.

    // Warm orange-red key from upper right — creates golden specular on metal frames
    const key = new THREE.PointLight(0xff8866, 4, 20);
    (key as unknown as { position: { set: (x: number, y: number, z: number) => void } })
      .position.set(4, 4, 4);
    envScene.add(key);

    // Cool blue fill from lower left — creates colour separation on metallic edges
    const fill = new THREE.PointLight(0x6688ff, 3, 20);
    (fill as unknown as { position: { set: (x: number, y: number, z: number) => void } })
      .position.set(-4, 2, -4);
    envScene.add(fill);

    // White overhead — soft diffuse lift for dark-colored frames
    const top = new THREE.PointLight(0xffffff, 2, 20);
    (top as unknown as { position: { set: (x: number, y: number, z: number) => void } })
      .position.set(0, 6, 0);
    envScene.add(top);

    // Warm kicker from front-right — adds subtle warmth to lens rims
    const kicker = new THREE.PointLight(0xffe0cc, 1.2, 20);
    (kicker as unknown as { position: { set: (x: number, y: number, z: number) => void } })
      .position.set(3, 1, 4);
    envScene.add(kicker);

    // Background sphere — MeshBasicMaterial baked into the IBL cubemap.
    // Medium-light cool grey (0xb0b8c4) provides enough diffuse IBL so that
    // dark plastic frames receive visible ambient light and show color/material
    // variation. The previous dark blue-grey (0x444466) produced near-black
    // shadows on dark frames, making them indistinguishable from each other.
    const envSphere = new THREE.Mesh(
      new THREE.SphereGeometry(10, 16, 16),
      new THREE.MeshBasicMaterial({ color: 0xb0b8c4, side: 1 }), // BackSide = 1
    );
    envScene.add(envSphere);

    const envTexture = pmremGenerator.fromScene(
      envScene as unknown as Parameters<typeof pmremGenerator.fromScene>[0],
    ).texture;

    scene.environment = envTexture;
    (scene as unknown as { environmentIntensity: number }).environmentIntensity =
      intensity;

    // Dispose temporary objects
    (envSphere.geometry as unknown as { dispose: () => void }).dispose();
    (envSphere.material as unknown as { dispose: () => void }).dispose();
    pmremGenerator.dispose();

    return envTexture as Texture;
  } catch {
    // PMREMGenerator unavailable — degrade gracefully; direct lights
    // still provide basic illumination.
    return null;
  }
}
